import React, { useEffect, useState, useMemo, useRef } from "react";
import { useAuth } from "react-oidc-context";
import Papa from "papaparse";
import ReactMarkdown from 'react-markdown';
import { Search, X, TrendingUp, Award, BarChart3, DollarSign, Settings, Sparkles } from 'lucide-react';
import AICuratedPicksCards from './AiCuratedPicksCards.jsx';
import TickerLink from './TickerLink.jsx';
import LogoLink from './LogoLink.jsx';
import { fetchManifest } from './membersApi.js';
import { isSigningOut, signOut } from './signOut.js';
import { isValidTicker, normalizeTicker, consumePendingDestination } from './ffvDestination.js';
import "./MemberLogin.css";
import { filterNotApplicable, notApplicableRows } from './dcfApplicability.js';
import { averageInRangeDiscount, discountDisplay, growthDisplay, isInRange, sortDcfRows, stepTwoCounts } from './dcfDisplay.js';
import './terminal.css';

// Reusable Search Bar Component
const SearchBar = ({ value, onChange, onClear, placeholder = "Search...", resultCount }) => {
  return (
    <div className="search-container">
      <div className="search-input-wrapper">
        <Search className="search-icon" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="search-input"
        />
        {value && (
          <button onClick={onClear} className="search-clear-button">
            <X />
          </button>
        )}
      </div>
      {value && (
        <div className={`search-result-counter ${resultCount === 0 ? 'no-results' : ''}`}>
          {resultCount > 0 ? (
            <span>Found <strong>{resultCount}</strong> {resultCount === 1 ? 'result' : 'results'}</span>
          ) : (
            <span>No results found</span>
          )}
        </div>
      )}
    </div>
  );
};

// Highlight matching text
const HighlightText = ({ text, searchTerm }) => {
  if (!searchTerm || !text) return <>{text}</>;

  const parts = text.toString().split(new RegExp(`(${searchTerm})`, 'gi'));

  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <span key={index} className="highlight-text">{part}</span>
        ) : (
          part
        )
      )}
    </>
  );
};

// Tab Navigation Component
const TabNav = ({ activeTab, setActiveTab, isMobile }) => {
  const tabs = [
    { id: 'ai-overview', label: 'AI Overview', icon: Sparkles },
    { id: 'quality', label: 'Quality + Value', icon: Award },
    { id: 'dcf', label: 'DCF Valuation', icon: DollarSign },  // ADD THIS LINE
    { id: 'momentum', label: 'Momentum', icon: BarChart3 },
    { id: 'technical', label: 'Technical Signals', icon: TrendingUp },
  ];

  return (
    <div className="t-tabs" role="tablist">
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;

        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => setActiveTab(tab.id)}
            className={`t-tab${isActive ? ' is-active' : ''}`}
          >
            <Icon size={isMobile ? 14 : 15} />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

// Market Switcher Component
const NOT_SUBSCRIBED_MESSAGE =
  'We could not confirm an active subscription for this account. If you subscribed recently, ' +
  'sign out and back in; otherwise contact ryan@tickrtrends.com.';
const LOAD_FAILED_MESSAGE = 'We could not load market data. Please try again.';
// Signed links last 5 minutes; refresh them a minute early (never sooner
// than every 30 s) so export buttons keep working on a page left open.
const LINK_REFRESH_LEAD_MS = 60 * 1000;
const LINK_REFRESH_FLOOR_MS = 30 * 1000;

const MarketSwitcher = ({ activeMarket, setActiveMarket }) => {
  const markets = [
    { id: 'sp500', label: 'S&P 500' },
    { id: 'nasdaq', label: 'NASDAQ' },
    { id: 'dow', label: 'DOW 30' },
  ];
  return (
    <div className="t-switch" role="group" aria-label="Market">
      {markets.map((m) => (
        <button
          key={m.id}
          onClick={() => setActiveMarket(m.id)}
          className={`t-switch__btn${activeMarket === m.id ? ' is-active' : ''}`}
          aria-pressed={activeMarket === m.id}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
};

const MembersPage = () => {
  const auth = useAuth();
  const [activeTab, setActiveTab] = useState('ai-overview');
  const [ffvData, setFfvData] = useState([]);
  const [momentumData, setMomentumData] = useState([]);
  const [changesData, setChangesData] = useState([]);
  const [qualityValueData, setQualityValueData] = useState([]);
  const [dogsData, setDogsData] = useState([]);
  const [technicalSignalsData, setTechnicalSignalsData] = useState([]);
  const [ffvAiAnalysis, setFfvAiAnalysis] = useState("");
  const [momentumAiAnalysis, setMomentumAiAnalysis] = useState("");
  const [ffvDownloadUrl, setFfvDownloadUrl] = useState("");
  const [momentumDownloadUrl, setMomentumDownloadUrl] = useState("");
  const [changesDownloadUrl, setChangesDownloadUrl] = useState("");
  const [qualityValueDownloadUrl, setQualityValueDownloadUrl] = useState("");
  const [dogsDownloadUrl, setDogsDownloadUrl] = useState("");
  const [technicalSignalsDownloadUrl, setTechnicalSignalsDownloadUrl] = useState("");
  // undefined while the manifest loads; null when the picks file is missing
  const [aiPicksUrl, setAiPicksUrl] = useState(undefined);
  const [aiAnalysisUrl, setAiAnalysisUrl] = useState(undefined);
  const [linksExpireAt, setLinksExpireAt] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [showSettings, setShowSettings] = useState(false);
  const [activeMarket, setActiveMarket] = useState('sp500');
  const [dcfData, setDcfData] = useState([]);
  // Tickers the FCF-based DCF does not apply to (financials, non-USD
  // reporters) - published as a separate array, shown in a panel, never
  // mixed into the valuation table (docs/dcf-financials-plan.md).
  const [dcfNotApplicable, setDcfNotApplicable] = useState([]);
  const [showNotApplicable, setShowNotApplicable] = useState(false);
  const [dcfSearchTerm, setDcfSearchTerm] = useState("");

  // Search states
  const [ffvSearchTerm, setFfvSearchTerm] = useState("");
  const [changesSearchTerm, setChangesSearchTerm] = useState("");
  const [momentumSearchTerm, setMomentumSearchTerm] = useState("");
  const [qualityValueSearchTerm, setQualityValueSearchTerm] = useState("");
  const [dogsSearchTerm, setDogsSearchTerm] = useState("");
  const [technicalSignalsSearchTerm, setTechnicalSignalsSearchTerm] = useState("");

  // /ffv/:ticker landing flow: ticker waiting to be located in the data,
  // "not covered" note, and settle-tracking so "absent" is only concluded
  // after every dataset fetch for a market has finished.
  const [pendingTicker, setPendingTicker] = useState(null);
  const [notCoveredTicker, setNotCoveredTicker] = useState(null);
  const [settledMarket, setSettledMarket] = useState(null);
  const loadIdRef = useRef(0);
  const fetchesRemainingRef = useRef(0);
  const marketProbeRef = useRef({ originMarket: null, checkedMarkets: [] });

  // Resize handler
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Pick up a /ffv/:ticker destination: from the URL when navigated directly,
  // else from sessionStorage when returning through the Cognito redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlTicker = params.get('ticker');
    const stored = consumePendingDestination();

    if (urlTicker && isValidTicker(urlTicker)) {
      setPendingTicker(normalizeTicker(urlTicker));
      return;
    }

    if (stored) {
      setPendingTicker(stored.ticker);
      // Restore UTM params onto the URL so GA attribution survives the
      // Cognito round-trip (its redirect_uri is a fixed URL with no query).
      if (stored.utm) {
        const merged = new URLSearchParams(window.location.search);
        new URLSearchParams(stored.utm).forEach((value, key) => {
          if (!merged.has(key)) merged.append(key, value);
        });
        merged.set('ticker', stored.ticker);
        window.history.replaceState(null, '', `${window.location.pathname}?${merged.toString()}`);
      }
    }
  }, []);

  // Locate the pending ticker: current market first, then the other market
  // (switching the toggle when the other market covers it), and only fall
  // back to "not covered" when it is in neither.
  useEffect(() => {
    if (!pendingTicker) return;

    const rowTicker = (row) => String(row.Ticker || row.ticker || '').trim().toUpperCase();
    const containsTicker = (data) => data.some((row) => rowTicker(row) === pendingTicker);

    const tabsInPriority = [
      ['quality', qualityValueData, setQualityValueSearchTerm],
      ['dcf', dcfData, setDcfSearchTerm],
      ['momentum', momentumData, setMomentumSearchTerm],
      ['technical', technicalSignalsData, setTechnicalSignalsSearchTerm],
    ];

    const match = tabsInPriority.find(([, data]) => containsTicker(data));
    if (match) {
      const [tabId, , setSearchTerm] = match;
      setActiveTab(tabId);
      setSearchTerm(pendingTicker);
      setPendingTicker(null);
      return;
    }

    // Absent so far — conclusive only once this market's fetches all settled
    if (settledMarket !== activeMarket) return;

    const probe = marketProbeRef.current;
    if (probe.originMarket === null) probe.originMarket = activeMarket;
    if (!probe.checkedMarkets.includes(activeMarket)) probe.checkedMarkets.push(activeMarket);

    const otherMarket = activeMarket === 'nasdaq' ? 'sp500' : 'nasdaq';
    if (!probe.checkedMarkets.includes(otherMarket)) {
      setActiveMarket(otherMarket);
      return;
    }

    // Neither market covers it — show the note and restore the original market
    setPendingTicker(null);
    setNotCoveredTicker(pendingTicker);
    if (probe.originMarket !== activeMarket) {
      setActiveMarket(probe.originMarket);
    }
  }, [pendingTicker, settledMarket, activeMarket, qualityValueData, dcfData, momentumData, technicalSignalsData]);

  // Refresh the export links from a new manifest before they expire. Only
  // the links change; the loaded tables stay as they are.
  useEffect(() => {
    if (!linksExpireAt) return undefined;
    const delay = Math.max(LINK_REFRESH_FLOOR_MS, linksExpireAt - Date.now() - LINK_REFRESH_LEAD_MS);
    const timer = setTimeout(() => {
      const loadId = loadIdRef.current;
      fetchManifest({ market: activeMarket, idToken: auth.user?.id_token })
        .then(({ datasets, expiresAt }) => {
          if (loadIdRef.current !== loadId) return; // the market changed meanwhile
          setFfvDownloadUrl(datasets.ffv?.url ?? '');
          setMomentumDownloadUrl(datasets.momentum?.url ?? '');
          setQualityValueDownloadUrl(datasets.quality_value?.url ?? '');
          setDogsDownloadUrl(datasets.dogs_of_dow?.url ?? '');
          setTechnicalSignalsDownloadUrl(datasets.technical_signals?.url ?? '');
          setAiAnalysisUrl(datasets.ai_ffv_md?.url ?? null);
          setLinksExpireAt(Date.parse(expiresAt));
        })
        .catch((err) => console.error('Members API link refresh failed:', err.code));
    }, delay);
    return () => clearTimeout(timer);
  }, [linksExpireAt, activeMarket, auth.user]);

  // Reload data when market switches
  useEffect(() => {
    if (auth.isAuthenticated) {
      setLoading(true);
      loadMarketData();
    }
  }, [activeMarket, auth.isAuthenticated]);

  useEffect(() => {
    // Never while signing out: that would race Cognito's /logout.
    if (!auth.isAuthenticated && !auth.isLoading && !isSigningOut()) {
      auth.signinRedirect();
    }
  }, [auth]);

  const loadMarketData = () => {
    setFfvAiAnalysis("");
    setMomentumAiAnalysis("");
    setError("");

    // Settle tracking for the /ffv landing flow: markSettled fires once per
    // tracked fetch (success or failure); when all have finished for this
    // load, the market is marked settled. A newer load supersedes older ones.
    const loadId = ++loadIdRef.current;
    const marketBeingLoaded = activeMarket;
    const TRACKED_FETCH_COUNT = 6; // 5 CSV datasets + 1 DCF JSON
    fetchesRemainingRef.current = TRACKED_FETCH_COUNT;
    setSettledMarket(null);
    const markSettled = () => {
      if (loadIdRef.current !== loadId) return;
      fetchesRemainingRef.current -= 1;
      if (fetchesRemainingRef.current <= 0) setSettledMarket(marketBeingLoaded);
    };

    // Member data comes only from the members API: it verifies the ID token
    // and custom:isSubscribed server-side and returns 5-minute signed links
    // from a fixed allowlist. The browser holds no AWS credentials.
    fetchManifest({ market: marketBeingLoaded, idToken: auth.user?.id_token })
      .then(({ datasets, expiresAt }) => {
        if (loadIdRef.current !== loadId) return; // a newer market load won
        loadCsvDataset(datasets.ffv, setFfvData, setFfvDownloadUrl, markSettled);
        loadCsvDataset(datasets.momentum, setMomentumData, setMomentumDownloadUrl, markSettled);
        loadCsvDataset(datasets.quality_value, setQualityValueData, setQualityValueDownloadUrl, markSettled);
        loadCsvDataset(datasets.dogs_of_dow, setDogsData, setDogsDownloadUrl, markSettled);
        loadCsvDataset(datasets.technical_signals, setTechnicalSignalsData, setTechnicalSignalsDownloadUrl, markSettled);
        loadMarkdownDataset(datasets.ai_ffv_md, setFfvAiAnalysis);
        loadMarkdownDataset(datasets.ai_momentum_md, setMomentumAiAnalysis);
        loadJsonDataset(datasets.dcf, setDcfData, markSettled);
        loadJsonDataset(datasets.dcf_not_applicable, data => setDcfNotApplicable(notApplicableRows(data)), () => {});
        setAiPicksUrl(datasets.ai_picks?.url ?? null);
        setAiAnalysisUrl(datasets.ai_ffv_md?.url ?? null);
        setLinksExpireAt(Date.parse(expiresAt));
      })
      .catch((err) => {
        if (loadIdRef.current !== loadId) return;
        console.error('Members API error:', err.code);
        setError(err.code === 'not_subscribed' ? NOT_SUBSCRIBED_MESSAGE : LOAD_FAILED_MESSAGE);
        setLoading(false);
      });
  };

  // Filtered data with memoization for performance
  const filteredFfvData = useMemo(() => {
    if (!ffvSearchTerm) return ffvData;

    const term = ffvSearchTerm.toLowerCase();
    return ffvData.filter(row =>
      (row.Ticker || row.ticker)?.toLowerCase().includes(term) ||
      (row['Current Price'] || row.current_price)?.toString().toLowerCase().includes(term) ||
      (row['Forward FV'] || row.forward_fv)?.toString().toLowerCase().includes(term) ||
      (row['$ Below FV'] || row.dollar_below_fv)?.toString().toLowerCase().includes(term) ||
      (row['% Below FV'] || row.percent_below_fv)?.toString().toLowerCase().includes(term)
    );
  }, [ffvData, ffvSearchTerm]);

  const filteredChangesData = useMemo(() => {
    if (!changesSearchTerm) return changesData;

    const term = changesSearchTerm.toLowerCase();
    return changesData.filter(row =>
      row.ticker?.toLowerCase().includes(term) ||
      row.status?.toLowerCase().includes(term) ||
      row.recommendation?.toLowerCase().includes(term) ||
      row.current_score?.toString().toLowerCase().includes(term)
    );
  }, [changesData, changesSearchTerm]);

  const filteredMomentumData = useMemo(() => {
    if (!momentumSearchTerm) return momentumData;

    const term = momentumSearchTerm.toLowerCase();
    return momentumData.filter(row =>
      Object.values(row).some(value =>
        value?.toString().toLowerCase().includes(term)
      )
    );
  }, [momentumData, momentumSearchTerm]);

    const filteredNotApplicable = useMemo(
      () => filterNotApplicable(dcfNotApplicable, dcfSearchTerm),
      [dcfNotApplicable, dcfSearchTerm]
    );

    const filteredDcfData = useMemo(() => {
    if (!dcfSearchTerm) return dcfData;

    const term = dcfSearchTerm.toLowerCase();
    return dcfData.filter(row =>
      row.ticker?.toLowerCase().includes(term) ||
      row.current_price?.toString().toLowerCase().includes(term) ||
      row.dcf_value?.toString().toLowerCase().includes(term)
    );
  }, [dcfData, dcfSearchTerm]);

  const filteredQualityValueData = useMemo(() => {
    if (!qualityValueSearchTerm) return qualityValueData;

    const term = qualityValueSearchTerm.toLowerCase();
    return qualityValueData.filter(row =>
      row.ticker?.toLowerCase().includes(term) ||
      row.quality_grade?.toLowerCase().includes(term) ||
      row.revenue_trend?.toLowerCase().includes(term)
    );
  }, [qualityValueData, qualityValueSearchTerm]);

  const filteredDogsData = useMemo(() => {
    if (!dogsSearchTerm) return dogsData.filter(row => row.ticker && row.ticker.trim() !== '');

    const term = dogsSearchTerm.toLowerCase();
    return dogsData.filter(row =>
      row.ticker && row.ticker.trim() !== '' &&
      (row.ticker?.toLowerCase().includes(term) ||
      row.company_name?.toLowerCase().includes(term))
    );
  }, [dogsData, dogsSearchTerm]);

  const filteredTechnicalSignalsData = useMemo(() => {
    if (!technicalSignalsSearchTerm) return technicalSignalsData;

    const term = technicalSignalsSearchTerm.toLowerCase();
    return technicalSignalsData.filter(row =>
      row.ticker?.toLowerCase().includes(term) ||
      row.signal?.toLowerCase().includes(term) ||
      row.signal_strength?.toString().includes(term)
    );
  }, [technicalSignalsData, technicalSignalsSearchTerm]);

  // Each dataset is { url, key, lastModified } from the manifest, or
  // null/undefined when the object is missing (or not in this market).
  const fetchSigned = (dataset) =>
    fetch(dataset.url).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response;
    });

  const loadMarkdownDataset = (dataset, setMarkdown) => {
    if (!dataset) return;
    fetchSigned(dataset)
      .then((response) => response.text())
      .then(setMarkdown)
      .catch((err) => console.error(`Error fetching ${dataset.key}:`, err.message));
  };

  const loadJsonDataset = (dataset, setData, onSettled = () => {}) => {
    if (!dataset) {
      onSettled();
      return;
    }
    fetchSigned(dataset)
      .then((response) => response.json())
      // Most undervalued first; rows the model flags (out of range or unreliable
      // inputs, decision 18) sort last so the top of the page is trustworthy.
      .then((jsonData) => setData(sortDcfRows(jsonData)))
      .catch((err) => console.error(`Error fetching ${dataset.key}:`, err.message))
      .finally(onSettled);
  };

  // Helper function to validate and clean data rows
  const isValidDataRow = (row) => {
    // Check for ticker in both formats: 'Ticker' (FFV) and 'ticker' (others)
    const tickerValue = row.Ticker || row.ticker;

    // Must have a valid ticker
    if (!tickerValue ||
        String(tickerValue).trim() === '' ||
        String(tickerValue) === 'NaN' ||
        String(tickerValue).toLowerCase() === 'nan') {
      return false;
    }

    // Check if at least ONE field has real numeric data
    // Look through all fields except ticker/Ticker
    const hasAnyValidNumber = Object.keys(row).some(key => {
      // Skip ticker fields (both formats)
      if (key === 'ticker' || key === 'Ticker') return false;

      const value = row[key];

      // Skip null, undefined, empty
      if (value === null || value === undefined || value === '') {
        return false;
      }

      const valueStr = String(value).trim().toLowerCase();

      // Filter out NaN variations and placeholder dashes
      if (valueStr === 'nan' ||
          valueStr === '$nan' ||
          valueStr === 'nan%' ||
          valueStr === '-' ||
          valueStr === '—' ||
          valueStr === 'â€"') {
        return false;
      }

      // Check if it's a valid number (including dollar amounts and percentages)
      // Remove $ and % symbols, then check if it's a number
      const numericValue = valueStr.replace(/[$%,]/g, '');
      return !isNaN(parseFloat(numericValue)) && isFinite(numericValue);
    });

    return hasAnyValidNumber;
  };

  const loadCsvDataset = (dataset, setData, setDownloadUrl, onSettled = () => {}) => {
    if (!dataset) {
      setLoading(false);
      onSettled();
      return;
    }

    setLastUpdate(new Date(dataset.lastModified).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }));
    // The tab's export link is the same signed URL (valid 5 minutes).
    setDownloadUrl(dataset.url);

    fetchSigned(dataset)
      .then((response) => response.text())
      .then((csvText) => {
        Papa.parse(csvText, {
          header: true,
          complete: (result) => {
            // Filter out invalid rows (empty tickers, all NaN values, etc.)
            setData(result.data.filter(isValidDataRow));
            setLoading(false);
            onSettled();
          },
        });
      })
      .catch(() => {
        setLoading(false);
        onSettled();
      });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'NEW': return '#10b981';
      case 'ACCELERATING': return '#3b82f6';
      case 'STABLE': return '#8b5cf6';
      case 'DECELERATING': return '#f59e0b';
      case 'DROPPED': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getRecommendationStyle = (rec) => {
    if (rec?.includes('STUDY')) return { color: '#10b981', fontWeight: '600' };
    if (rec?.includes('TRACK')) return { color: '#8b5cf6', fontWeight: '600' };
    if (rec?.includes('REVIEW')) return { color: '#f59e0b', fontWeight: '600' };
    if (rec?.includes('EXCLUDED')) return { color: '#ef4444', fontWeight: '600' };
    return {};
  };

  // Revokes the refresh token, clears the stored session (localStorage) and
  // ends the Cognito session - see signOut.js.
  const handleSignOut = () => signOut(auth);

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.')) {
      return;
    }

    try {
      const subscriptionId = auth.user?.profile?.['custom:subscriptionID'];

      if (!subscriptionId) {
        alert('No active subscription found. Please contact support if you believe this is an error.');
        return;
      }

      const idToken = auth.user?.id_token;

      if (!idToken) {
        alert('Authentication error. Please sign in again.');
        auth.signinRedirect();
        return;
      }

      const response = await fetch('https://6prhsyf8w1.execute-api.us-east-2.amazonaws.com/prod/unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ subscriptionId })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const endDate = new Date(data.subscription.currentPeriodEnd * 1000).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        alert(`Subscription canceled successfully.\n\nYou will retain full access until ${endDate}.\n\nNo further charges will be made after this date.`);
        setShowSettings(false);
      } else {
        alert(`Error: ${data.error || 'Failed to cancel subscription. Please try again or contact support.'}`);
      }
    } catch (error) {
      console.error('Cancellation error:', error);
      alert('An error occurred while canceling your subscription.\n\nPlease contact support at ryan@tickrtrends.com or try again later.');
    }
  };

  if (auth.isLoading || loading) {
    return (
      <div className="members-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading market data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="members-container">
        <div className="error-card">
          <h2>Error Loading Data</h2>
          <p>{error}</p>
          <button className="secondary-button" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="members-container">
      {/* Top Navigation Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        paddingBottom: '16px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
      }}>
        <LogoLink iconSize={24} />

        {/* Settings Icon - Right */}
        <button
          onClick={() => setShowSettings(!showSettings)}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#8a94a2',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
            e.currentTarget.style.color = '#8b5cf6';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#8a94a2';
          }}
        >
          <Settings size={20} />
          {!isMobile && <span style={{ fontSize: '0.9rem' }}>Settings</span>}
        </button>
      </div>

      {/* Settings Dropdown */}
      {showSettings && (
        <div style={{
          position: 'absolute',
          right: '20px',
          top: '60px',
          background: '#1a1f2e',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: '8px',
          padding: '16px',
          zIndex: 1000,
          minWidth: '200px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
        }}>
          {/* Account Info */}
          <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <div style={{ color: '#8a94a2', fontSize: '0.75rem', marginBottom: '4px' }}>ACCOUNT</div>
            <div style={{ color: '#ffffff', fontSize: '0.9rem', wordBreak: 'break-all' }}>
              {auth.user?.profile.email}
            </div>
          </div>

          {/* Cancel Subscription Button */}
          <button
            onClick={handleCancelSubscription}
            style={{
              width: '100%',
              padding: '10px',
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '6px',
              color: '#f59e0b',
              fontSize: '0.9rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '8px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(245, 158, 11, 0.2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(245, 158, 11, 0.1)';
            }}
          >
            Cancel Subscription
          </button>

          {/* Sign Out Button */}
          <button
            onClick={handleSignOut}
            style={{
              width: '100%',
              padding: '10px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '6px',
              color: '#ef4444',
              fontSize: '0.9rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
            }}
          >
            Sign Out
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div>
          <h1 className="t-page-title">
            Portfolio Intelligence
          </h1>
          <p className="t-secondary" style={{ margin: 0 }}>
            <span className="t-label">{activeMarket === 'nasdaq' ? 'NASDAQ' : activeMarket === 'dow' ? 'DOW 30' : 'S&P 500'}</span>
            <span style={{ margin: '0 6px' }}>·</span>AI-powered analysis
          </p>
        </div>

        <MarketSwitcher
          activeMarket={activeMarket}
          setActiveMarket={setActiveMarket}
          isMobile={isMobile}
        />
      </div>

      {/* Update Timestamp */}
      {lastUpdate && (
        <div className="t-status">
          <div className="t-status__group">
            <span className="t-label">Last updated</span>
            <span className="t-num" style={{ color: 'var(--t-text-2)', fontWeight: 500 }}>{lastUpdate}</span>
          </div>
          <div className="t-status__meta">
            <span>Market data <b>Real-time</b></span>
            <span>Analysis <b>AI-generated</b></span>
          </div>
        </div>
      )}

      {/* Ticker-not-covered note (from /ffv/:ticker landing) */}
      {notCoveredTicker && (
        <div className="t-note t-note--warn" style={{
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span>
            <strong>{notCoveredTicker}</strong> isn't currently covered by our screens — browse the full coverage below.
          </span>
          <button
            onClick={() => setNotCoveredTicker(null)}
            aria-label="Dismiss"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#fbbf24',
              cursor: 'pointer',
              fontSize: '1rem',
              padding: '4px',
              lineHeight: 1
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      {activeMarket !== 'dow' && <TabNav activeTab={activeTab} setActiveTab={setActiveTab} isMobile={isMobile} />}

      {/* DOW 30 - Dogs of the Dow Only */}
      {activeMarket === 'dow' && (
        <div>
          <div className="content-card">
            <div className="t-section-head">
              <h2>
                Dogs of the Dow Strategy
              </h2>
                <p>
                  Top 10 highest dividend-yielding Dow 30 stocks (Dow Jones Industrial Average). Equal-weight portfolio rebalanced annually.
                </p>

              <SearchBar
                value={dogsSearchTerm}
                onChange={setDogsSearchTerm}
                onClear={() => setDogsSearchTerm("")}
                placeholder="Search by ticker or company..."
                resultCount={filteredDogsData.length}
              />
            </div>

            {filteredDogsData.length > 0 ? (
              <>
                <div className="t-table-frame">
                  <table className="data-table" style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'center' }}>RANK</th>
                        <th style={{ textAlign: 'left' }}>COMPANY</th>
                        <th style={{ textAlign: 'left' }}>TICKER</th>
                        <th style={{ textAlign: 'right' }}>PRICE</th>
                        <th style={{ textAlign: 'right' }}>DIV/SHARE</th>
                        <th style={{ textAlign: 'right' }}>YIELD</th>
                        <th style={{ textAlign: 'right' }}>ALLOCATION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDogsData.map((row, index) => (
                        <tr key={index}>
                          <td className="t-cell-dim t-mono" style={{ textAlign: 'center' }}>
                            #{row.rank}
                          </td>
                          <td>
                            <HighlightText text={row.company_name} searchTerm={dogsSearchTerm} />
                          </td>
                          <td className="t-cell-ticker">
                            <TickerLink ticker={row.ticker}>
                              <HighlightText text={row.ticker} searchTerm={dogsSearchTerm} />
                            </TickerLink>
                          </td>
                          <td className="t-cell-num">
                            ${parseFloat(row.current_price).toFixed(2)}
                          </td>
                          <td className="t-cell-num">
                            ${parseFloat(row.annual_dividend).toFixed(2)}
                          </td>
                          <td className="t-cell-num" style={{ fontWeight: 600, color: 'var(--t-text)' }}>
                            {parseFloat(row.dividend_yield).toFixed(2)}%
                          </td>
                          <td className="t-cell-num t-cell-dim">
                            10%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="t-table-foot">
                  <div>
                    Portfolio: $100,000 | Equal Weight: $10,000 per stock
                  </div>
                  <a
                    href={dogsDownloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="t-btn"
                  >
                    Export Dogs Portfolio
                  </a>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <DollarSign className="empty-state-icon" />
                <p className="empty-state-title">
                  Loading Dogs of the Dow...
                </p>
              </div>
            )}
          </div>

          {/* Strategy Explanation */}
          <div className="content-card">
            <h2 className="t-h2">
              About the Strategy
            </h2>
            <div className="t-prose">
              <p>
                The <strong>Dogs of the Dow</strong> is a value investing strategy that focuses on high dividend yield stocks from the Dow Jones Industrial Average.
              </p>
              <p>
                <strong>How it works:</strong>
              </p>
              <ul>
                <li>Select the 10 highest dividend-yielding Dow 30 stocks at year-end</li>
                <li>Invest equal amounts ($10,000 each for a $100,000 portfolio)</li>
                <li>Hold for one full year</li>
                <li>Rebalance annually by selling and buying the new top 10</li>
              </ul>
              <p>
                <strong>Theory:</strong> High dividend yields often indicate temporarily undervalued blue-chip companies. The strategy assumes these quality companies will rebound, providing both income and capital appreciation through <em>mean reversion</em>.
              </p>
              <p className="t-secondary">
                Average Yield: {
                  dogsData.length > 0 && dogsData.filter(d => d.ticker).length > 0
                    ? (dogsData
                        .filter(d => d.ticker && !isNaN(parseFloat(d.dividend_yield)))
                        .reduce((sum, d) => sum + parseFloat(d.dividend_yield), 0)
                      / dogsData.filter(d => d.ticker && !isNaN(parseFloat(d.dividend_yield))).length
                      ).toFixed(2)
                    : '0.00'
                }%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* S&P 500 and NASDAQ Tabs */}
      {activeMarket !== 'dow' && (
        <>
      {/* AI OVERVIEW TAB */}
      {activeTab === 'ai-overview' && (
        <div>
          {/* AI Curated Picks Cards with market prop */}
          <AICuratedPicksCards market={activeMarket} url={aiPicksUrl} analysisUrl={aiAnalysisUrl} />

          {/* FFV AI Analysis */}
          {ffvAiAnalysis && (
            <div className="content-card">
              <div className="t-section-head">
                <h2>
                  Forward Fair Value Analysis
                </h2>
                <p>
                  AI-curated opportunities from our value screening methodology
                </p>
              </div>
              <div className="t-prose">
                <ReactMarkdown
                  components={{
                    h1: ({node, ...props}) => <h3 className="t-md-h1" {...props} />,
                    h2: ({node, ...props}) => <h4 className="t-md-h2" {...props} />,
                    }}
                >
                  {ffvAiAnalysis}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* Momentum AI Analysis */}
          {momentumAiAnalysis && (
            <div className="content-card">
              <div className="t-section-head">
                <h2>
                  Momentum Trend Analysis
                </h2>
                <p>
                  AI-powered momentum screening insights
                </p>
              </div>
              <div className="t-prose">
                <ReactMarkdown
                  components={{
                    h1: ({node, ...props}) => <h3 className="t-md-h1" {...props} />,
                    h2: ({node, ...props}) => <h4 className="t-md-h2" {...props} />,
                    }}
                >
                  {momentumAiAnalysis}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* Quick Stats Summary */}
          <div className="t-tile-grid" style={{ marginBottom: '20px' }}>
            <div>
              <div className="t-label">FFV Opportunities</div>
              <div className="t-value t-value--xl">{ffvData.length}</div>
            </div>
            <div>
              <div className="t-label">Quality Stocks</div>
              <div className="t-value t-value--xl">{qualityValueData.length}</div>
            </div>
            <div>
              <div className="t-label">Momentum Plays</div>
              <div className="t-value t-value--xl">{momentumData.length}</div>
            </div>
          </div>
        </div>
      )}

            {/* QUALITY + VALUE TAB */}
      {activeTab === 'quality' && (
        <div>
          <div className="content-card">
            <div className="t-section-head">
              <h2>
                Quality + Value Screen
              </h2>
              <p>
                High-quality businesses (80+ score) trading at significant discounts. Combines fundamental analysis with FFV valuation.
              </p>

              <SearchBar
                value={qualityValueSearchTerm}
                onChange={setQualityValueSearchTerm}
                onClear={() => setQualityValueSearchTerm("")}
                placeholder="Search by ticker, grade, or trend..."
                resultCount={filteredQualityValueData.length}
              />
            </div>

            {filteredQualityValueData.length > 0 ? (
              <div className="t-table-frame t-table-frame--tall">
                <table className="data-table" style={{ marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>TICKER</th>
                      <th style={{ textAlign: 'right' }}>PRICE</th>
                      <th style={{ textAlign: 'right' }}>FFV</th>
                      <th style={{ textAlign: 'right' }}>DISCOUNT</th>
                      <th style={{ textAlign: 'center' }}>QUALITY</th>
                      <th style={{ textAlign: 'right' }}>REV GROWTH</th>
                      <th style={{ textAlign: 'right' }}>DEBT/EQ</th>
                      <th style={{ textAlign: 'right' }}>MARGIN</th>
                      <th style={{ textAlign: 'right' }}>RSI-14</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQualityValueData.map((row, index) => {
                      const discount = parseFloat(row.percent_below_fv);
                      const qualityScore = parseInt(row.quality_score);
                      const isHighQuality = qualityScore >= 80;
                      const isDeepDiscount = discount > 30;

                      return (
                        <tr
                          key={index}
                          style={{
                            // high quality + deep discount: a quiet left rule, no tinted row
                            boxShadow: (isHighQuality && isDeepDiscount)
                              ? 'inset 2px 0 0 var(--t-accent)'
                              : 'none'
                          }}
                          title={(isHighQuality && isDeepDiscount) ? 'High quality (80+) at a deep discount (>30%)' : undefined}
                        >
                          <td className="t-cell-ticker">
                            <TickerLink ticker={row.ticker}>
                              <HighlightText text={row.ticker} searchTerm={qualityValueSearchTerm} />
                            </TickerLink>
                          </td>
                          <td className="t-cell-num">
                            ${parseFloat(row.current_price).toFixed(2)}
                          </td>
                          <td className="t-cell-num">
                            ${parseFloat(row.forward_fv).toFixed(2)}
                          </td>
                          <td style={{
                            textAlign: 'right',
                            fontWeight: '600',
                            color: discount > 0 ? '#10b981' : '#ef4444'
                          }}>
                            {discount.toFixed(1)}%
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span className="t-badge" style={{ fontWeight: isHighQuality ? 700 : 600, color: isHighQuality ? 'var(--t-text)' : 'var(--t-text-2)' }}>
                              {row.quality_score} {row.quality_grade}
                            </span>
                          </td>
                          <td style={{
                            textAlign: 'right',
                            color: parseFloat(row.revenue_growth_3yr) > 10 ? '#10b981' :
                                   parseFloat(row.revenue_growth_3yr) > 0 ? '#c5d1e8' : '#ef4444'
                          }}>
                            {row.revenue_growth_3yr ? `${row.revenue_growth_3yr}%` : '-'}
                          </td>
                          <td style={{
                            textAlign: 'right',
                            color: parseFloat(row.debt_to_equity) < 0.5 ? '#10b981' :
                                   parseFloat(row.debt_to_equity) < 1.0 ? '#c5d1e8' : '#f59e0b'
                          }}>
                            {row.debt_to_equity || '-'}
                          </td>
                          <td style={{
                            textAlign: 'right',
                            color: parseFloat(row.gross_margin) > 50 ? '#10b981' : '#c5d1e8'
                          }}>
                            {row.gross_margin ? `${row.gross_margin}%` : '-'}
                          </td>
                            <td style={{
                              textAlign: 'right',
                              fontWeight: '600',
                              color: !row.rsi_14 ? '#6b7280' :
                                    parseFloat(row.rsi_14) < 30 ? '#10b981' :
                                    parseFloat(row.rsi_14) > 70 ? '#ef4444' :
                                    '#c5d1e8'
                            }}>
                              {row.rsi_14 ? parseFloat(row.rsi_14).toFixed(1) : '-'}
                            </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <Search className="empty-state-icon" />
                <p className="empty-state-title">
                  {qualityValueSearchTerm
                    ? `No stocks match "${qualityValueSearchTerm}"`
                    : "Loading quality data..."}
                </p>
              </div>
            )}

            <div className="t-table-foot">
              <div>
                Displaying {filteredQualityValueData.length} of {qualityValueData.length} quality opportunities
              </div>
              <a
                href={qualityValueDownloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="t-btn"
              >
                Export Quality + Value Data
              </a>
            </div>
          </div>

          {/* Quality Stats Summary */}
          {qualityValueData.length > 0 && (
            <div className="content-card">
              <h2 className="t-h2">
                Quality Breakdown
              </h2>
              <div className="t-tile-grid">
                <div>
                  <div className="t-label">A-Grade (80+)</div>
                  <div className="t-value t-value--lg">
                    {qualityValueData.filter(r => parseInt(r.quality_score) >= 80).length}
                  </div>
                </div>
                <div>
                  <div className="t-label">Deep Discount (&gt;30%)</div>
                  <div className="t-value t-value--lg">
                    {qualityValueData.filter(r => parseFloat(r.percent_below_fv) > 30).length}
                  </div>
                </div>
                <div>
                  <div className="t-label">Growing (Rev &gt;10%)</div>
                  <div className="t-value t-value--lg">
                    {qualityValueData.filter(r => parseFloat(r.revenue_growth_3yr) > 10).length}
                  </div>
                </div>
                <div>
                  <div className="t-label">Low Debt (&lt;0.5)</div>
                  <div className="t-value t-value--lg">
                    {qualityValueData.filter(r => parseFloat(r.debt_to_equity) < 0.5).length}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* MOMENTUM TAB */}
      {activeTab === 'momentum' && (
        <div>
          <div className="content-card">
            <div className="t-section-head">
              <h2>
                Momentum Screening Results
              </h2>
              <p>
                Stocks meeting dual value + momentum criteria
              </p>

              <SearchBar
                value={momentumSearchTerm}
                onChange={setMomentumSearchTerm}
                onClear={() => setMomentumSearchTerm("")}
                placeholder="Search momentum data..."
                resultCount={filteredMomentumData.length}
              />
            </div>

            {filteredMomentumData.length > 0 ? (
              <>
                <div className="t-table-frame t-table-frame--mid">
                  <table className="data-table" style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        {momentumData.length > 0 && Object.keys(momentumData[0]).map((key) => (
                          <th key={key}>
                            {key.replace(/_/g, ' ')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMomentumData.map((row, index) => (
                        <tr key={index}>
                          {Object.entries(row).map(([key, value], i) => (
                            <td key={i} style={{
                              color: '#c5d1e8',
                              fontWeight: key === 'ticker' ? '600' : 'normal'
                            }}>
                              {key.toLowerCase() === 'ticker' ? (
                                <TickerLink ticker={value}>
                                  <HighlightText text={value} searchTerm={momentumSearchTerm} />
                                </TickerLink>
                              ) : (
                                <HighlightText text={value} searchTerm={momentumSearchTerm} />
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="t-table-foot">
                  <div>
                    Displaying {filteredMomentumData.length} of {momentumData.length} opportunities
                  </div>
                  <a
                    href={momentumDownloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="t-btn"
                  >
                    Export Momentum Data
                  </a>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <Search className="empty-state-icon" />
                <p className="empty-state-title">
                  No momentum data matches "<strong>{momentumSearchTerm}</strong>"
                </p>
                <p className="empty-state-subtitle">
                  Try adjusting your search terms
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TECHNICAL SIGNALS TAB */}
      {activeTab === 'technical' && (
        <div>
          <div className="content-card">
            <div className="t-section-head">
              <h2>
                Technical Signals - Golden & Death Crosses
              </h2>
              <p>
                Moving average crossover signals indicating potential trend changes. Golden Cross (50-day MA crosses above 200-day MA) suggests bullish momentum. Death Cross suggests bearish momentum.
              </p>

              <SearchBar
                value={technicalSignalsSearchTerm}
                onChange={setTechnicalSignalsSearchTerm}
                onClear={() => setTechnicalSignalsSearchTerm("")}
                placeholder="Search by ticker or signal type..."
                resultCount={filteredTechnicalSignalsData.length}
              />
            </div>

            {filteredTechnicalSignalsData.length > 0 ? (
              <>
                <div className="t-table-frame">
                  <table className="data-table" style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>TICKER</th>
                        <th style={{ textAlign: 'center' }}>SIGNAL</th>
                        <th style={{ textAlign: 'right' }}>DAYS AGO</th>
                        <th style={{ textAlign: 'right' }}>STRENGTH</th>
                        <th style={{ textAlign: 'right' }}>PRICE</th>
                        <th style={{ textAlign: 'right' }}>MA 50</th>
                        <th style={{ textAlign: 'right' }}>MA 200</th>
                        <th style={{ textAlign: 'right' }}>SPREAD %</th>
                        <th style={{ textAlign: 'right' }}>DISCOUNT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTechnicalSignalsData.map((row, index) => {
                        const isGolden = row.signal === 'GOLDEN';
                        const signalColor = isGolden ? '#10b981' : '#ef4444';
                        const strengthScore = parseFloat(row.signal_strength) || 0;

                        return (
                          <tr key={index}>
                            <td className="t-cell-ticker">
                              <TickerLink ticker={row.ticker}>
                                <HighlightText text={row.ticker} searchTerm={technicalSignalsSearchTerm} />
                              </TickerLink>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <span className={`t-badge ${isGolden ? 't-badge--up' : 't-badge--down'}`}>
                                <HighlightText text={row.signal} searchTerm={technicalSignalsSearchTerm} />
                              </span>
                            </td>
                            <td className="t-cell-num">
                              {row.days_ago || '-'}
                            </td>
                            <td style={{
                              textAlign: 'right',
                              color: strengthScore >= 70 ? '#10b981' :
                                     strengthScore >= 50 ? '#f59e0b' : '#ef4444',
                              fontWeight: '600'
                            }}>
                              {row.signal_strength}
                            </td>
                            <td className="t-cell-num">
                              ${row.current_price}
                            </td>
                            <td className="t-cell-num t-cell-dim">
                              ${row.ma_50}
                            </td>
                            <td className="t-cell-num t-cell-dim">
                              ${row.ma_200}
                            </td>
                            <td style={{
                              textAlign: 'right',
                              color: parseFloat(row.ma_spread) > 0 ? '#10b981' : '#ef4444',
                              fontWeight: '500'
                            }}>
                              {row.ma_spread}%
                            </td>
                            <td style={{
                              textAlign: 'right',
                              color: row.discount_to_ffv && parseFloat(row.discount_to_ffv) > 0 ? '#10b981' : '#6b7280',
                              fontWeight: '500'
                            }}>
                              {row.discount_to_ffv ? `${row.discount_to_ffv}%` : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="t-table-foot">
                  <div>
                    Displaying {filteredTechnicalSignalsData.length} of {technicalSignalsData.length} signals
                  </div>
                  {technicalSignalsDownloadUrl && (
                    <a
                      href={technicalSignalsDownloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="t-btn"
                    >
                      Export Technical Signals
                    </a>
                  )}
                </div>
              </>
            ) : (
              <div className="empty-state">
                <TrendingUp className="empty-state-icon" />
                <p className="empty-state-title">
                  No technical signals detected
                </p>
                <p className="empty-state-subtitle">
                  Try running the technical scanner script
                </p>
              </div>
            )}
          </div>

          {/* Strategy Explanation */}
          <div className="content-card">
            <h2 className="t-h2">
              About Technical Signals
            </h2>
            <div className="t-prose">
              <p>
                <strong>Golden Cross</strong> and <strong>Death Cross</strong> are powerful moving average crossover signals used to identify major trend changes.
              </p>
              <p>
                <strong>How to use these signals:</strong>
              </p>
              <ul>
                <li><strong style={{ color: '#10b981' }}>Golden Cross:</strong> Buy signal when 50-day MA crosses above 200-day MA. Indicates potential long-term uptrend.</li>
                <li><strong style={{ color: '#ef4444' }}>Death Cross:</strong> Sell signal when 50-day MA crosses below 200-day MA. Indicates potential long-term downtrend.</li>
                <li><strong>Strength Score:</strong> Higher scores (70+) indicate stronger, more reliable signals. Focus on recent crosses (≤10 days).</li>
                <li><strong>Combine with Value:</strong> Best results when golden crosses align with deep FFV discounts (30%+).</li>
              </ul>
              <p>
                <strong>Best practices:</strong> Use as confirmation alongside value and quality screens. Set stop-loss orders below the 50-day MA to manage risk.
              </p>
              <p className="t-secondary">
                {technicalSignalsData.length > 0 ? (
                  <>
                    Golden Crosses: {technicalSignalsData.filter(d => d.signal === 'GOLDEN').length} •
                    Death Crosses: {technicalSignalsData.filter(d => d.signal === 'DEATH').length}
                  </>
                ) : (
                  'No signals currently detected'
                )}
              </p>
            </div>
          </div>
        </div>
      )}
        </>
      )}
            {/* DCF VALUATION TAB */}
      {activeTab === 'dcf' && (
        <div>
          <div className="content-card">
            <div className="t-section-head">
              <h2>
                DCF Intrinsic Value Analysis
              </h2>
              <p>
                Discounted Cash Flow valuation showing intrinsic value based on 5-year projected cash flows.
                Sorted by highest discount to DCF value (most undervalued first); rows the model flags sort last.
              </p>

              <SearchBar
                value={dcfSearchTerm}
                onChange={setDcfSearchTerm}
                onClear={() => setDcfSearchTerm("")}
                placeholder="Search by ticker or value..."
                resultCount={filteredDcfData.length}
              />
            </div>

            {filteredDcfData.length > 0 ? (
              <>
                <div className="t-table-frame t-table-frame--tall">
                  <table className="data-table" style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>TICKER</th>
                        <th style={{ textAlign: 'right' }}>CURRENT PRICE</th>
                        <th style={{ textAlign: 'right' }}>DCF VALUE</th>
                        <th style={{ textAlign: 'right' }}>DISCOUNT</th>
                        <th style={{ textAlign: 'right' }}>WACC</th>
                        <th style={{ textAlign: 'right' }}>FCF GROWTH</th>
                        <th style={{ textAlign: 'right' }}>ENTERPRISE VALUE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDcfData.map((row, index) => {
                        const discount = parseFloat(row.discount_to_dcf || 0);
                        const dcfValue = parseFloat(row.dcf_value || 0);
                        const currentPrice = parseFloat(row.current_price || 0);
                        const wacc = parseFloat(row.wacc || 0);
                        // Step-2 fields (2026-09-12): out-of-range rows are published flagged,
                        // the displayed discount is capped at ±200 %, growth carries its provenance.
                        const inRange = isInRange(row);
                        const discountView = discountDisplay(row);
                        const growthView = growthDisplay(row);

                        // Color coding: 20-50% = good value, 50-80% = warning, 80%+ = extreme caution
                        let discountColor = '#6b7280';
                        if (discount > 0 && discount < 20) discountColor = '#c5d1e8';
                        else if (discount >= 20 && discount < 50) discountColor = '#10b981';
                        else if (discount >= 50 && discount < 80) discountColor = '#f59e0b';
                        else if (discount >= 80) discountColor = '#ef4444';
                        if (!inRange) discountColor = '#8a94a2';

                        return (
                          <tr
                            key={index}
                            style={{
                              background: discount >= 20 && discount < 50 && inRange
                                ? 'rgba(16, 185, 129, 0.05)'
                                : 'transparent',
                              opacity: inRange ? 1 : 0.7
                            }}
                          >
                            <td className="t-cell-ticker">
                              <TickerLink ticker={row.ticker}>
                                <HighlightText text={row.ticker} searchTerm={dcfSearchTerm} />
                              </TickerLink>
                              {!inRange && (
                                <span
                                  className="t-badge t-badge--warn t-badge--pill"
                                  title={`Beyond model range: ${discountView.rangeLabel}. The number is shown but not ranked.`}
                                  style={{ marginLeft: '8px', verticalAlign: 'middle' }}
                                >
                                  beyond model range
                                </span>
                              )}
                            </td>
                            <td className="t-cell-num">
                              ${currentPrice.toFixed(2)}
                            </td>
                            <td className="t-cell-num" style={{ fontWeight: 600, color: 'var(--t-text)' }}>
                              ${dcfValue.toFixed(2)}
                            </td>
                            <td style={{
                              textAlign: 'right',
                              fontWeight: '700',
                              color: discountColor,
                              fontSize: '0.95rem'
                            }}>
                              <span title={discountView.beyond ? `Model output: ${discount.toFixed(1)}% (display capped at ±200%)` : undefined}>
                                {discountView.text}
                              </span>
                              {discount >= 80 && inRange && <span style={{ marginLeft: '4px', fontSize: '0.8rem' }}>⚠️</span>}
                            </td>
                            <td className="t-cell-num t-cell-dim">
                              {wacc.toFixed(2)}%
                            </td>
                            <td
                              title={growthView.title || undefined}
                              style={{
                                textAlign: 'right',
                                color: growthView.positive ? '#10b981' : '#ef4444',
                                cursor: growthView.mark ? 'help' : 'default'
                              }}
                            >
                              {growthView.text}
                              {growthView.mark && (
                                <span style={{ marginLeft: '4px', color: '#f59e0b', fontSize: '0.8rem' }} aria-label={growthView.title}>
                                  {growthView.mark}
                                </span>
                              )}
                            </td>
                            <td style={{ textAlign: 'right', color: '#8a94a2', fontSize: '0.9rem' }}>
                              ${row.enterprise_value ? (parseFloat(row.enterprise_value) / 1e9).toFixed(2) + 'B' : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="t-table-foot">
                  <div>
                    Displaying {filteredDcfData.length} of {dcfData.length} DCF valuations
                    {averageInRangeDiscount(filteredDcfData) !== null && ` • Avg Discount (in range): ${averageInRangeDiscount(filteredDcfData).toFixed(1)}%`}
                    {stepTwoCounts(filteredDcfData).outOfRange > 0 && ` • Beyond model range: ${stepTwoCounts(filteredDcfData).outOfRange}`}
                    {stepTwoCounts(filteredDcfData).growthAtBound > 0 && ` • Growth at model bound (▲▼): ${stepTwoCounts(filteredDcfData).growthAtBound}`}
                    {stepTwoCounts(filteredDcfData).growthAssumed > 0 && ` • Growth assumed (≈): ${stepTwoCounts(filteredDcfData).growthAssumed}`}
                    {dcfNotApplicable.length > 0 && ` • Model not applicable: ${dcfNotApplicable.length}`}
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <DollarSign className="empty-state-icon" />
                <p className="empty-state-title">
                  {dcfSearchTerm
                    ? `No DCF valuations match "${dcfSearchTerm}"`
                    : "Loading DCF valuations..."}
                </p>
                {dcfSearchTerm && filteredNotApplicable.length > 0 && (
                  <p style={{ color: '#f59e0b', fontSize: '0.9rem', marginTop: '8px' }}>
                    {filteredNotApplicable.map(r => r.ticker).join(', ')}: the DCF model is not applicable — see the panel below.
                  </p>
                )}
              </div>
            )}

            {/* Model not applicable (financials, non-USD reporters): flagged, never silently dropped */}
            {dcfNotApplicable.length > 0 && (
              <div style={{
                marginTop: '20px',
                border: '1px solid rgba(245, 158, 11, 0.25)',
                borderRadius: '8px',
                background: 'rgba(245, 158, 11, 0.04)'
              }}>
                <button
                  type="button"
                  onClick={() => setShowNotApplicable(v => !v)}
                  aria-expanded={showNotApplicable}
                  style={{
                    width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
                    color: '#c5d1e8', padding: '12px 16px', cursor: 'pointer', fontSize: '0.95rem',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}
                >
                  <span>
                    <strong>Model not applicable ({filteredNotApplicable.length}{dcfSearchTerm ? ` of ${dcfNotApplicable.length}` : ''})</strong>
                    <span style={{ color: '#8a94a2', marginLeft: '10px', fontSize: '0.85rem' }}>
                      FCF-based DCF is not run for financials or non-USD reporters — flagged here instead of published as a valuation
                    </span>
                  </span>
                  <span style={{ color: '#8a94a2' }}>{showNotApplicable ? '▲' : '▼'}</span>
                </button>
                {showNotApplicable && (
                  <div style={{ padding: '0 16px 12px 16px', overflowX: 'auto' }}>
                    <table className="data-table" style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left' }}>TICKER</th>
                          <th style={{ textAlign: 'left' }}>SECTOR</th>
                          <th style={{ textAlign: 'right' }}>PRICE</th>
                          <th style={{ textAlign: 'left' }}>WHY THE MODEL IS NOT APPLIED</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredNotApplicable.map(row => (
                          <tr key={row.ticker}>
                            <td className="t-cell-ticker">
                              <TickerLink ticker={row.ticker}>
                                <HighlightText text={row.ticker} searchTerm={dcfSearchTerm} />
                              </TickerLink>
                            </td>
                            <td className="t-cell-dim">{row.sector || '—'}</td>
                            <td className="t-cell-num">
                              {row.currentPrice != null ? `$${row.currentPrice.toFixed(2)}` : '—'}
                            </td>
                            <td style={{ color: '#8a94a2', fontSize: '0.85rem', maxWidth: '520px' }}>
                              <span style={{ color: '#f59e0b', fontWeight: '600', marginRight: '6px' }}>{row.label}</span>
                              {row.text}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* DCF Methodology Explanation */}
          <div className="content-card">
            <h2 className="t-h2">
              Understanding DCF Analysis
            </h2>
            <div className="t-prose">
              <p>
                <strong>Discounted Cash Flow (DCF)</strong> calculates intrinsic value by projecting future cash flows and discounting them to present value.
              </p>
              <p>
                <strong>Our DCF Model Parameters:</strong>
              </p>
              <ul>
                <li><strong>Projection Period:</strong> 5 years of free cash flow forecasts</li>
                <li><strong>Growth Rate:</strong> Based on 3-year average revenue growth (floored at 0%, capped at 25%)</li>
                <li><strong>WACC:</strong> Company-specific weighted average cost of capital (5-15% range)</li>
                <li><strong>Terminal Value:</strong> Perpetuity growth model at 2.5%</li>
                <li><strong>Debt Adjustment:</strong> Uses actual total debt from financial statements</li>
              </ul>
              <p>
                <strong>How to interpret:</strong>
              </p>
              <ul>
                <li><strong style={{ color: '#10b981' }}>20-50% discount:</strong> Potential value opportunity - cross-reference with Quality scores</li>
                <li><strong style={{ color: '#f59e0b' }}>50-80% discount:</strong> High risk/reward - verify fundamentals carefully</li>
                <li><strong style={{ color: '#ef4444' }}>80%+ discount:</strong> Extreme caution - likely regulatory, accounting, or business model concerns</li>
              </ul>
              <p className="t-note t-note--warn" style={{ marginBottom: '12px' }}>
                <strong>⚠️ Important:</strong> DCF is conservative by design and works best for mature, stable companies.
                High-growth tech and international stocks (especially Chinese) often show extreme discounts due to factors
                DCF cannot capture: regulatory risk, accounting transparency, network effects, and future product potential.
                Financials (banks, insurers, asset managers), REITs and companies reporting in non-USD currencies are not valued
                at all — their operating cash flow is not distributable free cash flow — and are listed under
                &quot;Model not applicable&quot; instead.
              </p>
              <p className="t-secondary">
                {dcfData.length > 0 ? (
                  <>
                    Total Valuations: {dcfData.length} •
                    Potential Value (20-50%): {dcfData.filter(d => isInRange(d) && parseFloat(d.discount_to_dcf) >= 20 && parseFloat(d.discount_to_dcf) < 50).length} •
                    High Risk (80%+): {dcfData.filter(d => isInRange(d) && parseFloat(d.discount_to_dcf) >= 80).length}
                    {stepTwoCounts(dcfData).outOfRange > 0 && ` • Beyond model range: ${stepTwoCounts(dcfData).outOfRange}`}
                    {dcfNotApplicable.length > 0 && ` • Model not applicable: ${dcfNotApplicable.length}`}
                  </>
                ) : (
                  'Loading DCF data...'
                )}
              </p>
            </div>
          </div>

          {/* DCF Statistics Summary */}
          {dcfData.length > 0 && (
            <div className="content-card">
              <h2 className="t-h2">
                DCF Breakdown
              </h2>
              <div className="t-tile-grid">
                <div>
                  <div className="t-label">Value Opportunities (20-50%)</div>
                  <div className="t-value t-value--lg">
                    {dcfData.filter(r => isInRange(r) && parseFloat(r.discount_to_dcf) >= 20 && parseFloat(r.discount_to_dcf) < 50).length}
                  </div>
                </div>
                <div>
                  <div className="t-label">Fair Value (0-20%)</div>
                  <div className="t-value t-value--lg">
                    {dcfData.filter(r => isInRange(r) && parseFloat(r.discount_to_dcf) >= 0 && parseFloat(r.discount_to_dcf) < 20).length}
                  </div>
                </div>
                <div>
                  <div className="t-label">Overvalued (&lt;0%)</div>
                  <div className="t-value t-value--lg">
                    {dcfData.filter(r => isInRange(r) && parseFloat(r.discount_to_dcf) < 0).length}
                  </div>
                </div>
                {stepTwoCounts(dcfData).outOfRange > 0 && (
                  <div>
                    <div className="t-label">Beyond model range</div>
                    <div className="t-value t-value--lg t-warn">
                      {stepTwoCounts(dcfData).outOfRange}
                    </div>
                    <div className="t-secondary">
                      Out of the model's range or unreliable inputs (leverage, FCF history); shown, sorted last
                    </div>
                  </div>
                )}
                <div>
                  <div className="t-label">Avg WACC</div>
                  <div className="t-value t-value--lg">
                    {(dcfData.reduce((sum, r) => sum + parseFloat(r.wacc || 0), 0) / dcfData.length).toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Disclaimer Footer */}
      <div className="t-footer">
        <p>
          <strong>Disclaimer:</strong> This platform provides research tools and quantitative analysis for educational purposes only.
          All data is provided "as is" without warranty. This does not constitute investment advice,
          and past performance does not guarantee future results. Consult a licensed financial advisor before making investment decisions.
        </p>
        <p>
          Data updates: Weekly (Monday pre-market)
        </p>
      </div>
    </div>
  );
};

export default MembersPage;